import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ROTULO_STATUS_ALUNO } from '@arena/shared/schemas';
import { alunosApi } from '../api/alunos.js';
import { useAtrasado } from '../agenda/useAtrasado.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { Botao, Escolhas, Kpi, Nota, Quem, Status, Tabela, Vazio } from '../ui/primitivos.jsx';
import { dataCurta, SITUACAO_DO_STATUS, telefoneFormatado } from './formatos.js';
import { ModalDeNovoAluno } from './ModalDeNovoAluno.jsx';
import { ModalDoAluno } from './ModalDoAluno.jsx';
// 09 · importação
import { ModalDeImportacao } from '../importacao/ModalDeImportacao.jsx';

/**
 * `#v-alunos` — busca, filtros por status, tabela e os KPIs do topo.
 *
 * As colunas "Plano", "Turmas" e "Situação financeira" são do protótipo,
 * mas os documentos que as preenchem (10 e 12) ainda não existem — a
 * mesma convenção do doc 08 para as abas de `#m-aluno`: a coluna fica
 * visível, com "—" no lugar do dado, em vez de desaparecer e a tela
 * parecer outra.
 *
 * Ver: Arena_backend/Docs/plano/08-alunos.md
 */

const FILTROS = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'Ativo', rotulo: 'Ativos' },
  { valor: 'Experimental', rotulo: 'Experimentais' },
  { valor: 'Inativo', rotulo: 'Inativos' },
] as const satisfies readonly { valor: string; rotulo: string }[];

type FiltroDeStatus = (typeof FILTROS)[number]['valor'];

export function PaginaDeAlunos() {
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  const { pode } = useSessao();
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState<FiltroDeStatus>('todos');
  const buscaAtrasada = useAtrasado(busca.trim(), 300);

  const indicadores = useQuery({
    queryKey: ['alunos', 'indicadores'],
    queryFn: () => alunosApi.indicadores(),
  });

  const listagem = useInfiniteQuery({
    queryKey: ['alunos', 'listar', buscaAtrasada, filtro],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      alunosApi.listar({
        busca: buscaAtrasada || undefined,
        status: filtro === 'todos' ? undefined : filtro,
        cursor: pageParam,
        limite: 25,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (ultima) => ultima.proximoCursor ?? undefined,
  });

  const podeCriar = pode('alunos', 'criar');

  function mudarParams(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }
  const abrirNovo = () => mudarParams((p) => p.set('novo', '1'));
  const fecharNovo = () => mudarParams((p) => p.delete('novo'));
  const abrirFicha = (id: string) => mudarParams((p) => p.set('aluno', id));
  const fecharFicha = () => mudarParams((p) => p.delete('aluno'));

  const novo = params.get('novo') === '1';
  const alunoAberto = params.get('aluno');
  // 09 · importação — o modal mora na URL, como os outros.
  const importando = params.get('importar') === '1';
  const abrirImportacao = () => mudarParams((p) => p.set('importar', '1'));
  const fecharImportacao = () => mudarParams((p) => p.delete('importar'));

  const itens = listagem.data?.pages.flatMap((p) => p.itens) ?? [];
  const total = listagem.data?.pages[0]?.total ?? 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Alunos</h1>
          <p>Base única de pessoas: mensalista, avulso e experimental.</p>
        </div>
        {podeCriar && (
          <div className="head-actions">
            {/* 09 · importação: o botão de `#v-alunos` no protótipo, antes de "Novo aluno". */}
            <Botao variante="contorno" icone="clip" onClick={abrirImportacao}>
              Importar CSV
            </Botao>
            <Botao icone="plus" onClick={abrirNovo}>
              Novo aluno
            </Botao>
          </div>
        )}
      </div>

      <Nota>
        <b>Módulo 1 — Cadastro de alunos.</b> Busca sem acento e por trecho do nome, telefone ou
        CPF. O ciclo Experimental → Ativo → Inativo → Cancelado é o que alimenta os indicadores de
        conversão e cancelamento. No cancelamento o cadastro é anonimizado, não apagado, para
        preservar o histórico financeiro exigido por lei.
      </Nota>

      {indicadores.isPending && (
        <div className="grid g4">
          {[0, 1, 2, 3].map((i) => (
            <EsqueletoDeCard key={i} linhas={2} />
          ))}
        </div>
      )}
      {indicadores.isError && (
        <Erro
          tipo={
            indicadores.error instanceof ErroDeRede
              ? 'rede'
              : indicadores.error instanceof ErroDeApi && indicadores.error.ehPermissao
                ? 'permissao'
                : 'falha'
          }
          onTentarDeNovo={() => void indicadores.refetch()}
        />
      )}
      {indicadores.data && (
        <div className="grid g4">
          <Kpi
            valor={indicadores.data.ativos}
            rotulo="Ativos"
            tendencia={indicadores.data.ativacoesNoMes > 0 ? 'alta' : 'estavel'}
            variacao={`${indicadores.data.ativacoesNoMes} no mês`}
          />
          <Kpi
            valor={indicadores.data.experimentais}
            rotulo="Experimentais em aberto"
            tendencia="estavel"
            // A taxa de conversão (o "72%" do protótipo) é cálculo do
            // documento 16 — aqui só a contagem que já existe.
            variacao={`${indicadores.data.convertidosNoMes} convertidos no mês`}
          />
          <Kpi valor={indicadores.data.inativos} rotulo="Inativos" tendencia="estavel" />
          <Kpi
            valor={indicadores.data.canceladosNoMes}
            rotulo="Cancelados no mês"
            tendencia={indicadores.data.canceladosNoMes > 0 ? 'baixa' : 'estavel'}
            variacao="motivo obrigatório registrado"
          />
        </div>
      )}

      <article className="card">
        <div className="card-head">
          <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap' }}>
            <label className="search" style={{ maxWidth: 280 }}>
              <Icone nome="search" />
              <input
                id="busca-aluno"
                placeholder="Nome, telefone ou CPF"
                aria-label="Buscar aluno"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            </label>
            <Escolhas
              aria-label="Filtrar por situação"
              opcoes={FILTROS}
              valor={filtro}
              onChange={setFiltro}
            />
          </div>
          <span className="hint">
            {listagem.isPending
              ? ''
              : `${itens.length} de ${total} cadastro${total === 1 ? '' : 's'}`}
          </span>
        </div>

        {listagem.isPending && <EsqueletoDeTabela colunas={8} />}

        {listagem.isError && (
          <Erro
            tipo={
              listagem.error instanceof ErroDeRede
                ? 'rede'
                : listagem.error instanceof ErroDeApi && listagem.error.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(listagem.error)}
            onTentarDeNovo={() => void listagem.refetch()}
          />
        )}

        {listagem.data && itens.length === 0 && (
          <Vazio>
            {buscaAtrasada || filtro !== 'todos'
              ? 'Nenhum aluno encontrado com este filtro.'
              : 'Nenhum aluno cadastrado ainda.'}
          </Vazio>
        )}

        {listagem.data && itens.length > 0 && (
          <>
            <Tabela
              colunas={[
                'Aluno',
                'Telefone',
                'Plano',
                'Turmas',
                'Situação financeira',
                'Status',
                'Desde',
                '',
              ]}
            >
              {itens.map((aluno) => (
                <tr
                  key={aluno.id}
                  className="clickable"
                  style={{ cursor: 'pointer' }}
                  onClick={() => abrirFicha(aluno.id)}
                >
                  <td>
                    <Quem
                      nome={aluno.nome}
                      detalhe={aluno.apelido ? `"${aluno.apelido}"` : undefined}
                    />
                  </td>
                  <td>{telefoneFormatado(aluno.telefone)}</td>
                  <td className="sub">—</td>
                  <td className="sub">—</td>
                  <td className="sub">—</td>
                  <td>
                    <Status situacao={SITUACAO_DO_STATUS[aluno.status]}>
                      {ROTULO_STATUS_ALUNO[aluno.status]}
                    </Status>
                  </td>
                  <td>{dataCurta(aluno.desde)}</td>
                  <td>
                    <Icone nome="go" className="ic go" />
                  </td>
                </tr>
              ))}
            </Tabela>

            {listagem.hasNextPage && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0 2px' }}>
                <Botao
                  variante="contorno"
                  pequeno
                  onClick={() => void listagem.fetchNextPage()}
                  disabled={listagem.isFetchingNextPage}
                >
                  {listagem.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
                </Botao>
              </div>
            )}
          </>
        )}
      </article>

      {novo && podeCriar && (
        <ModalDeNovoAluno
          onFechar={fecharNovo}
          onCriado={(id) =>
            // Uma troca só: separar em `fecharNovo(); abrirFicha(id);`
            // aplicaria a segunda sobre o `params` de ANTES da primeira
            // — os dois `set/delete` precisam do mesmo `URLSearchParams`.
            mudarParams((p) => {
              p.delete('novo');
              p.set('aluno', id);
            })
          }
        />
      )}

      {alunoAberto && <ModalDoAluno id={alunoAberto} onFechar={fecharFicha} />}

      {importando && podeCriar && (
        <ModalDeImportacao
          onFechar={fecharImportacao}
          onAbrir={(id) => navegar(`/alunos/importacoes/${id}`)}
        />
      )}
    </>
  );
}
